import styled from "styled-components";
import { FiAward } from "react-icons/fi";
import {
  Section,
  Eyebrow,
  SectionTitle,
  Head,
  Tag,
  Glass,
  Reveal,
  useReveal,
} from "../../studio/ui";
import { experiences, education, achievements } from "../../data/constants";

/* ============================================================
   Experience — Divine Futurism.
   Ascending divine ladder timeline · education relics ·
   achievement certifications. Glass · gold · serif.
============================================================ */

/* ---------- timeline : ascending divine ladder ---------- */
const Timeline = styled.div`
  position: relative;
  padding-left: 46px;

  /* luminous gold line */
  &::before {
    content: "";
    position: absolute;
    left: 14px;
    top: 6px;
    bottom: 6px;
    width: 2px;
    background: linear-gradient(
      180deg,
      transparent,
      ${({ theme }) => theme.gold} 8%,
      ${({ theme }) => theme.goldSoft} 50%,
      ${({ theme }) => theme.violet} 92%,
      transparent
    );
    box-shadow: 0 0 18px ${({ theme }) => theme.goldGlow};
  }

  @media ${({ theme }) => theme.bp.sm} {
    padding-left: 34px;
    &::before {
      left: 9px;
    }
  }
`;

const Entry = styled.div`
  position: relative;
  margin-bottom: 34px;

  &:last-child {
    margin-bottom: 0;
  }

  /* glowing gold diamond node on the line */
  &::before {
    content: "";
    position: absolute;
    left: -39px;
    top: 26px;
    width: 13px;
    height: 13px;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.goldSoft},
      ${({ theme }) => theme.gold}
    );
    transform: rotate(45deg);
    border-radius: 2px;
    box-shadow: 0 0 0 4px ${({ theme }) => theme.bg},
      0 0 18px ${({ theme }) => theme.goldGlow},
      0 0 30px ${({ theme }) => theme.violetGlow};
  }

  @media ${({ theme }) => theme.bp.sm} {
    &::before {
      left: -31px;
    }
  }
`;

const Card = styled(Glass)`
  padding: 26px 28px;

  @media ${({ theme }) => theme.bp.md} {
    padding: 22px 20px;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const RoleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Role = styled.h3`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 600;
  font-size: 23px;
  line-height: 1.15;
  color: ${({ theme }) => theme.white};
`;

const CurrentBadge = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  color: ${({ theme }) => theme.goldSoft};
  border: 1px solid ${({ theme }) => theme.borderHi};
  background: linear-gradient(
    100deg,
    rgba(201, 168, 76, 0.12),
    rgba(107, 63, 160, 0.16)
  );
  box-shadow: 0 0 16px ${({ theme }) => theme.goldGlow};
`;

const Company = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.gold};
  margin-top: 5px;
`;

const Meta = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.textMuted};
  text-align: right;
  line-height: 1.6;
  white-space: nowrap;

  span {
    display: block;
    color: ${({ theme }) => theme.textFaint};
  }

  @media ${({ theme }) => theme.bp.sm} {
    text-align: left;
  }
`;

const Desc = styled.p`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 14.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.textMuted};
  margin-top: 14px;
`;

const Bullets = styled.ul`
  list-style: none;
  margin: 16px 0 4px;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const Bullet = styled.li`
  position: relative;
  padding-left: 20px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 14.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.textMuted};

  &::before {
    content: "";
    position: absolute;
    left: 2px;
    top: 8px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.gold};
    box-shadow: 0 0 8px ${({ theme }) => theme.goldGlow};
  }
`;

const Skills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
`;

/* ---------- sub headings ---------- */
const SubHead = styled.h3`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 600;
  font-size: clamp(24px, 3.4vw, 32px);
  color: ${({ theme }) => theme.white};
  margin: 72px 0 28px;

  em {
    font-style: italic;
    color: ${({ theme }) => theme.gold};
  }

  @media ${({ theme }) => theme.bp.md} {
    margin: 52px 0 22px;
  }
`;

/* ---------- education grid ---------- */
const EduGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;

  @media ${({ theme }) => theme.bp.md} {
    grid-template-columns: 1fr;
  }
`;

const EduCard = styled(Glass)`
  padding: 22px 22px;
  display: flex;
  gap: 16px;
`;

const EduImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.bgDeep};
`;

const EduBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const School = styled.h4`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
  color: ${({ theme }) => theme.white};
`;

const Degree = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 13.5px;
  color: ${({ theme }) => theme.textMuted};
  margin-top: 4px;
`;

const EduMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
`;

const EduDate = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11.5px;
  color: ${({ theme }) => theme.textFaint};
`;

const Grade = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.gold};
`;

/* ---------- achievements grid ---------- */
const AchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media ${({ theme }) => theme.bp.sm} {
    grid-template-columns: 1fr;
  }
`;

const AchCard = styled(Glass)`
  padding: 20px 20px;
  display: flex;
  gap: 14px;
`;

const AchIcon = styled.div`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  color: ${({ theme }) => theme.gold};
  border: 1px solid ${({ theme }) => theme.border};
  background: rgba(201, 168, 76, 0.06);
  box-shadow: 0 0 14px ${({ theme }) => theme.goldGlow};

  svg {
    font-size: 18px;
  }
`;

const AchBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const AchTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 14.5px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.white};
`;

const AchDetail = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.textMuted};
  margin-top: 4px;
`;

/* ---------- per-card reveal subcomponents ---------- */
const JobEntry = ({ exp, delay }) => {
  const [ref, shown] = useReveal();
  return (
    <Entry>
      <Reveal ref={ref} $in={shown} $dir="right" $delay={delay}>
        <Card>
          <CardTop>
            <div>
              <RoleRow>
                <Role>{exp.role}</Role>
                {exp.tag && <CurrentBadge>{exp.tag}</CurrentBadge>}
              </RoleRow>
              <Company>{exp.company}</Company>
            </div>
            <Meta>
              {exp.date}
              <span>{exp.location}</span>
            </Meta>
          </CardTop>

          {exp.desc && <Desc>{exp.desc}</Desc>}

          {exp.bullets && exp.bullets.length > 0 && (
            <Bullets>
              {exp.bullets.map((b, i) => (
                <Bullet key={i}>{b}</Bullet>
              ))}
            </Bullets>
          )}

          {exp.skills && exp.skills.length > 0 && (
            <Skills>
              {exp.skills.map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
            </Skills>
          )}
        </Card>
      </Reveal>
    </Entry>
  );
};

const EduEntry = ({ edu, delay, dir }) => {
  const [ref, shown] = useReveal();
  return (
    <Reveal ref={ref} $in={shown} $dir={dir} $delay={delay}>
      <EduCard>
        <EduImg src={edu.img} alt={edu.school} />
        <EduBody>
          <School>{edu.school}</School>
          <Degree>{edu.degree}</Degree>
          <EduMeta>
            <EduDate>{edu.date}</EduDate>
            <Grade>{edu.grade}</Grade>
          </EduMeta>
        </EduBody>
      </EduCard>
    </Reveal>
  );
};

const AchEntry = ({ ach, delay, dir }) => {
  const [ref, shown] = useReveal();
  return (
    <Reveal ref={ref} $in={shown} $dir={dir} $delay={delay}>
      <AchCard>
        <AchIcon>
          <FiAward />
        </AchIcon>
        <AchBody>
          <AchTitle>{ach.title}</AchTitle>
          <AchDetail>{ach.detail}</AchDetail>
        </AchBody>
      </AchCard>
    </Reveal>
  );
};

const Experience = () => {
  return (
    <Section id="experience">
      <Head>
        <Eyebrow>Experience</Eyebrow>
        <SectionTitle>
          The <em>path</em> walked
        </SectionTitle>
      </Head>

      <Timeline>
        {experiences.map((exp, i) => (
          <JobEntry key={exp.id} exp={exp} delay={i * 0.06} />
        ))}
      </Timeline>

      <SubHead>
        <em>Education</em>
      </SubHead>
      <EduGrid>
        {education.map((edu, i) => (
          <EduEntry
            key={edu.id}
            edu={edu}
            delay={i * 0.05}
            dir={i % 2 === 0 ? "left" : "right"}
          />
        ))}
      </EduGrid>

      <SubHead>
        Achievements &amp; <em>Certifications</em>
      </SubHead>
      <AchGrid>
        {achievements.map((ach, i) => (
          <AchEntry
            key={ach.title}
            ach={ach}
            delay={i * 0.04}
            dir={i % 2 === 0 ? "left" : "right"}
          />
        ))}
      </AchGrid>
    </Section>
  );
};

export default Experience;
